import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchAuthors, createAuthor, updateAuthor, deleteAuthor, fetchOrganizations } from '../api/apiService';

export default function AuthorsPage() {
  // 状态管理
  const [authors, setAuthors] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 对话框状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentAuthor, setCurrentAuthor] = useState({ id: null, name: '', uid: '', avatar: '', organizationId: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [authorsData, orgsData] = await Promise.all([
        fetchAuthors(),
        fetchOrganizations()
      ]);
      setAuthors(authorsData);
      setOrganizations(orgsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenCreateDialog = () => {
    setIsEditing(false);
    setCurrentAuthor({ id: null, name: '', uid: '', avatar: '', organizationId: '' });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (author) => {
    setIsEditing(true);
    setCurrentAuthor({ ...author, organizationId: author.organizationId || '' });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!currentAuthor.organizationId) {
        alert('请选择一个社团');
        return;
    }
    try {
      const authorData = {
        name: currentAuthor.name,
        uid: currentAuthor.uid,
        avatar: currentAuthor.avatar,
        organizationId: currentAuthor.organizationId
      };

      if (isEditing) {
        await updateAuthor(currentAuthor.id, authorData);
      } else {
        await createAuthor(authorData);
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      alert(`保存失败: ${err.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('您确定要删除这位作者吗？')) {
      try {
        await deleteAuthor(id);
        loadData();
      } catch (err) {
        alert(`删除失败: ${err.message}`);
      }
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'avatar',
      headerName: '头像',
      width: 80,
      renderCell: (params) => <Avatar src={params.value ? `//${params.value}` : ''} />,
      sortable: false,

    },
    { field: 'name', headerName: '作者名称', width: 150 },
    { field: 'uid', headerName: 'B站UID', width: 120 },
    {
      field: 'organization',
      headerName: '所属社团',
      flex: 1,
      valueGetter: (params) => params.row?.organization?.name || '',
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleOpenEditDialog(params.row)} color="primary"><EditIcon /></IconButton>
          <IconButton onClick={() => handleDelete(params.row.id)} color="error"><DeleteIcon /></IconButton>
        </>
      ),
    },
  ];

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">加载数据失败: {error}</Alert>;

  return (
    <Box sx={{ height: 'calc(100vh - 128px)', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">作者管理</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          新增作者
        </Button>
      </Box>
      <DataGrid rows={authors} columns={columns} />
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>{isEditing ? '编辑作者' : '新增作者'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="作者名称" fullWidth variant="standard" value={currentAuthor.name} onChange={(e) => setCurrentAuthor({ ...currentAuthor, name: e.target.value })} />
          <TextField margin="dense" label="B站UID" fullWidth variant="standard" value={currentAuthor.uid} onChange={(e) => setCurrentAuthor({ ...currentAuthor, uid: e.target.value })} />
          <TextField margin="dense" label="头像地址" fullWidth variant="standard" value={currentAuthor.avatar} onChange={(e) => setCurrentAuthor({ ...currentAuthor, avatar: e.target.value })} />
          <FormControl variant="standard" fullWidth margin="dense">
            <InputLabel>所属社团</InputLabel>
            <Select
              value={currentAuthor.organizationId}
              onChange={(e) => setCurrentAuthor({ ...currentAuthor, organizationId: e.target.value })}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSave}>保存</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}